import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import ReactSelect from 'react-select';

import Auth from '../../utils/Auth';
import { NPOS_APP, TRUE_APP } from '../../utils/constants';
import { filterRestrictedStores, shouldDisableStoreSelection } from '../../utils/ej-utils';
import { unPadStoreNumber, sortStoresByNumber } from '../../utils/formatting';
import { getApplicationSource, isRetailLogin } from '../../utils/iframeMessaging';
import { storeViewsByCountry } from '../../utils/service-calls';
import nikeStoreCountries from '../../utils/static/countriesWithNikeStores';

import './Select.css';
// Everything you need to run a storeSelect
const defaultStoreviewsFields = [
  'storeNumber',
  'name',
  'facilityType',
  'company',
  'address',
  'currencies',
  'id',
  'locale',
  'name',
  'phone',
  'timezone',
  'localizations',
  'storeConcept'
];

const urlEnv = () => (process.env.NODE_ENV === 'production' ? 'prod' : 'test');

class StoreSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      disableCountry: false,
      disableCountryAndStore: false,
      fetching: false,
      isMounted: false,
      storesInCountry: [],
    };
  }

  componentDidMount = async () => {
    this.setState({ isMounted: true });
    const retailLogin = isRetailLogin();
    if (this.isChinaUrl() && !retailLogin) {
      const country = Auth.getCountriesByAuthGroup(nikeStoreCountries.filter((countries) => countries.value === 'CHN'));
      this.props.selectCountry(country[0]);
      this.getStoresByCountry(country[0]);
      this.setStateFn({ disableCountry: true });
    } else if (retailLogin) {
      const appSource = getApplicationSource();
      this.setStateFn({ fetching: true });
      const country = await Auth.getCountriesForRetailLogin(nikeStoreCountries);
      if (country.length === 1) {
        if (country[0]?.value === 'CHN' && !this.isChinaUrl()) { // Only china URL should be able to use China Option In Dropdown
          this.setStateFn({ disableCountryAndStore: true, fetching: false });
          return;
        }
        this.props.selectCountry(country[0]);
        this.getStoresByCountry(country[0]);
        this.setStateFn({ disableCountry: true, disableCountryAndStore: appSource === TRUE_APP });
        if (appSource === NPOS_APP) this.getStoresByCountry(this.props.selectedCountry);
      }
    } else {
      this.getStoresByCountry(this.props.selectedCountry);
    }
  };

  componentWillUnmount = () => {
    this.setState({ isMounted: false });
  }

  getStoresByCountry = (country) => {
    if (!country?.value) {
      return;
    }
    // For NPOS fetch stores that are open
    const appSourceNpos = getApplicationSource();
    const isPublished = appSourceNpos === NPOS_APP ? true : this.props.isPublished;
    this.setStateFn({ errorMessage: '', fetching: true });
    storeViewsByCountry(country.value, isPublished, this.getStoreviewsFields())
      .then((res) => {
        if (!res.body || !res.body.objects || !Array.isArray(res.body.objects)) {
          // eslint-disable-next-line fp/no-throw
          throw new Error(res.statusText);
        }
        const storesInCountry = res.body.objects.map((storeInCountry) => {
          const label = `${unPadStoreNumber(storeInCountry.storeNumber)} - ${storeInCountry.name}`;
          return ({ full: storeInCountry, label, value: storeInCountry.id });
        });
        const sortedStores = sortStoresByNumber(storesInCountry);
        if (isRetailLogin()) {
          const appSource = getApplicationSource();
          const storeId = Auth.getStoreId();
          const currentStores = storesInCountry.find((store) => store.value === storeId);
          this.props.selectStore(currentStores);
          this.setStateFn({ disableCountryAndStore: shouldDisableStoreSelection(appSource) });
          return this.setStateFn({ fetching: false, storesInCountry: filterRestrictedStores(sortedStores, currentStores, appSource) });
        } else {
          return this.setStateFn({ fetching: false, storesInCountry: sortedStores });
        }
      })
      .catch((err) => this.setStateFn({ errorMessage: err.message, fetching: false, storesInCountry: [] }));
  };

  // to be pared down, yet.
  getStoreviewsFields = () => (this.props.storeviewsFields ? [...new Set([...defaultStoreviewsFields, ...this.props.storeviewsFields])] : null);

  // Check if the user is logged in to China EJ
  isChinaUrl = () => window.location.hostname === `ej.c4c-retail-comm-svc-${urlEnv()}.nikecloud.com.cn`;

  setStateFn = (state, callback) => (this.state.isMounted ? this.setState(state, callback) : null);

  // If a storeSelect is trying to use their own set of fields, make sure they have at least defaultStoreviewsFields the storeSelect itself works.
  // If they don't pass their own storeviewsFields, then don't pass any so we still get all attributes from storeviews. They may not be ready for the results

  render = () => (
    <section className="ta-sm-l">
      {
        <>
          <p className="body-2 mt2-sm">{this.props.RL_Country}</p>
          <ReactSelect
            classNamePrefix="react-select"
            clearable={false}
            id="countrySelect"
            inputId="countrySelect-input"
            isDisabled={this.props.isDisabled || this.state.disableCountryAndStore || this.state.disableCountry}
            options={Auth.getCountriesByAuthGroup(nikeStoreCountries.filter((countries) => countries.value !== 'CHN'))}
            styles={{
              container: (styles) => ({ ...styles, zIndex: 110 }),
              control: (styles) => ({
                ...styles,
                borderColor: (this.props.isRequired && !this.props.selectedCountry?.value) ? '#D43F21' : 'lightgrey',
                padding: '0px 0px 0px 3px',
              }),
            }}
            value={this.props.selectedCountry}
            onChange={(selectedOption) => {
              this.getStoresByCountry(selectedOption);
              this.props.selectCountry(selectedOption);
            }}
          />
          {this.props.isRequired && this.props.formErrors.selectedCountry
          && <p className="text-color-error body-4 mt1-sm">{this.props.formErrors.selectedCountry}</p>}
        </>
      }
      {!this.state.fetching
        ? (
          <>
            <p className="body-2 mt2-sm">{this.props.RL_Store}</p>
            <ReactSelect
              classNamePrefix="react-select"
              clearable={false}
              id="storeSelect"
              inputId="storeSelect-input"
              isDisabled={(!this.props.selectedCountry || this.props.isDisabled) || this.state.disableCountryAndStore}
              options={this.state.storesInCountry}
              styles={{
                container: (styles) => ({ ...styles, zIndex: 100 }),
                control: (styles) => ({
                  ...styles,
                  borderColor: (this.props.isRequired && !this.props.selectedStore?.value) ? '#D43F21' : 'lightgrey',
                  padding: '0px 0px 0px 3px',
                }),
              }}
              value={this.props.selectedStore || null}
              onChange={(selectedOption) => this.props.selectStore(selectedOption)}
            />
            {this.props.isRequired && this.props.formErrors.selectedStore
            && <p className="text-color-error body-4 mt1-sm">{this.props.formErrors.selectedStore}</p>}
          </>
        )
        : <article className="va-sm-t ta-sm-c mt-6-sm mb2-sm"><Loading /></article>}
      {this.state.errorMessage && <aside className="text-color-error body-4 mt1-sm">{this.state.errorMessage}</aside>}
    </section>
  );
}

StoreSelect.defaultProps = {
  countryRestriction: null,
  formErrors: {},
  isDisabled: false,
  isPublished: false,
  isRequired: false,
  selectedCountry: {},
  selectedStore: { full: {} },
  storeviewsFields: null,
};

StoreSelect.propTypes = {
  countryRestriction: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  formErrors: PropTypes.shape(),
  isDisabled: PropTypes.bool,
  isPublished: PropTypes.bool,
  isRequired: PropTypes.bool,
  RL_Country: PropTypes.string.isRequired,
  RL_Store: PropTypes.string.isRequired,
  selectCountry: PropTypes.func.isRequired,
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  selectedStore: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  selectStore: PropTypes.func.isRequired,
  storeviewsFields: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = ({ localizedBody }) => ({
  RL_Country: localizedBody.RL_Country,
  RL_Store: localizedBody.RL_Store
});

export default connect(mapStateToProps)(StoreSelect);
